<template>
    <v-app-bar
        density="prominent"
        shrink-on-scroll
        name="app-bar"
        elevation="0"
        :height="$vuetify.display.mdAndUp && contact ? 26 : 0"
        :extension-height="$vuetify.display.lgAndUp ? 45 : 32"
        :color="shrink ? 'white' : 'transparent'"
        :class="{ shrink, inverted: inverted, 'green-bar' : usePage().props.useGreenBar }"
        v-scroll="onScroll"
    >
        <div class="app-bar-container bg-md-transparent ga-8 px-4 top-bar" :class="usePage().props.useGreenBar ? 'bg-cis-accent-color text-white' : 'bg-white text-cis-accent-color'">
            <div v-if="contact?.phone" class="tb-item">
                <v-icon :icon="mdiPhone" size="15px"></v-icon>
                <span>{{ contact.phone }}</span>
            </div>
            <div v-if="contact?.openingHours" class="tb-item">
                <v-icon :icon="mdiClock" size="15px"></v-icon>
                <span>{{ contact.openingHours }}</span>
            </div>
            <div v-if="contact?.email" class="tb-item">
                <v-icon :icon="mdiEmail" size="15px"></v-icon>
                <span>{{ contact.email }}</span>
            </div>

        </div>
        <template #extension>
            <div class="app-bar-container px-4" :class="inverted ? 'inverted' : ''">
                <div v-if="!$vuetify.display.mdAndUp" :class="$page.props.partner?.eshop_link ? 'w-25' : 'flex-grow-1'">
                    <v-app-bar-nav-icon
                        @click="drawer = !drawer"
                        :color="inverted ? 'white' : ''"
                    ></v-app-bar-nav-icon>
                </div>
                <div :class="{ 'flex-grow-1': $vuetify.display.mdAndUp }" class="d-flex align-center">
                    <Link
                        href="/"
                        @click="drawer = false"
                        class="text-decoration-none d-inline-flex"
                    >
                        <v-img
                            :src="logo.src"
                            :alt="logo.alt"
                            :aspect-ratio="25 / 8"
                            :min-width="usePage().props.useGreenBar ? '16em' : '12em'"
                            :max-height="$vuetify.display.mdAndUp ? 82 : 62"
                        />
                    </Link>
                </div>
                <div v-if="$vuetify.display.mdAndUp" class="app-bar-menu-items">
                    <template v-for="menuItem in menu">
                        <div
                            v-if="menuItem.type === 'link'"
                            @click="handleClick(menuItem)"
                            :id="`menu-item-${menuItem.id}`"
                            class="app-bar-menu-item"
                            :class="menuItem.url === $page.props.url ? 'active' : ''"
                        >
                            {{ menuItem.title }}
                        </div>
                        <cart-button v-else @click.prevent="handleClick(menuItem)" :id="`menu-item-${menuItem.id}`" :title="menuItem.title"></cart-button>
                        <v-menu
                            v-if="menuItem.children.length"
                            :activator="`#menu-item-${menuItem.id}`"
                            open-on-hover
                        >
                            <v-list>
                                <v-list-item
                                    v-for="(item, index) in menuItem.children"
                                    :key="index"
                                    :value="index"
                                    @click="handleClick(menuItem)"
                                >
                                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                    <cart-button v-if="$page.props.partner?.eshop_link" :link="$page.props.partner?.eshop_link" :title="$t('homepage-groups-section.shop_here')"></cart-button>
                </div>
                <template v-if="!$vuetify.display.mdAndUp">
                    <div v-if="$page.props.partner?.eshop_link || menu.filter(el => el.type === 'cart').length" class="w-25 d-flex justify-end">
                        <template v-if="$page.props.partner?.eshop_link">
                            <cart-button :link="$page.props.partner?.eshop_link" small></cart-button>
                        </template>
                        <template v-else v-for="item in menu.filter(el => el.type === 'cart')" :key="item.id">
                            <cart-button @click.prevent="handleClick(item)" :color="!shrink ? 'white' : 'cis-accent-color'" small></cart-button>
                        </template>
                    </div>
                    <div v-else class="flex-grow-1">
                        <div class="equal-spacer"></div>
                    </div>
                </template>
            </div>
        </template>
    </v-app-bar>
    <v-navigation-drawer
        v-if="$vuetify.display.mdAndDown"
        v-model="drawer"
        :width="$vuetify.display.width"
    >
        <v-list>
            <template v-for="(item, index) in menu" :key="index">
                <template v-if="item.type === 'link'">
                    <v-list-item v-if="!item.children.length" :value="index" @click="handleClick(item)">
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                    </v-list-item>
                    <v-list-group v-else>
                        <template v-slot:activator="{ props }">
                            <v-list-item v-bind="props" :title="item.title"></v-list-item>
                        </template>
                        <template
                            v-for="(child, i) in item.children"
                            :key="i">
                            <v-list-item
                                @click="handleClick(child)"
                                :title="child.title"
                                :value="child.title"
                            ></v-list-item>
                        </template>
                    </v-list-group>
                </template>
            </template>
        </v-list>
    </v-navigation-drawer>
</template>

<script setup>
import { computed, inject, ref } from 'vue';
import { router, Link, usePage } from '@inertiajs/vue3';
import { mdiCart, mdiClock, mdiEmail, mdiPhone } from '@mdi/js';
import { useDisplay } from 'vuetify';
import 'swiper/css';
import 'swiper/css/navigation';
import { urlWithParameter } from '../../utils/image.js';
import CartButton from './navigation/CartButton.vue';

const props = defineProps({
    menu: Array,
    menuModel: Array,
    activeMenu: Object,
    hasBackground: Boolean,
});
const inverted = computed(() => {
    return props.hasBackground && !shrink.value;
});
const emit = defineEmits(['closeLeftDrawer', 'closeRightDrawer']);
const contact = computed(() => usePage().props.contact);
const menu = computed(() => {
    return usePage().props.menu
        ?.filter(el => el.position === 'top')
        .sort((a, b) => a.sort - b.sort)
        .filter(el => {
            const partner = usePage().props.partner?.eshop_link;
            return !(partner && el.type === 'cart');
        });
})
const drawer = ref();
const shrink = ref(false);
const logo = computed(() => {
    const obj = {
        src: `/logo${inverted.value ? '_inverted' : ''}.svg?max-width=200`,
        alt: 'Logo',
    };
    const settingsLogo = usePage().props.logo;
    if (settingsLogo) {
        obj.src = urlWithParameter(settingsLogo.url, 'max-width=200');
        if (settingsLogo.alt) {
            obj.alt = settingsLogo.alt;
        }
    }
    return obj;
});

const garlandLogo = computed(() => {
    return {
        src: `/logo_inverted.svg?max-width=200`,
        alt: 'Logo',
    };
});
const onScroll = () => {
    if (Math.round(window.scrollY) >= 1) {
        shrink.value = true;
        return;
    }
    shrink.value = false;
};
const route = inject('route');
const handleClick = (menuItem) => {
    if (menuItem.route) {
        router.visit(route(menuItem.route))
    }
    if (menuItem.url.includes('https://')) {
        window.location.href = menuItem.url;
    } else {
        router.visit(menuItem.url);
    }
    drawer.value = false;
};
</script>

<style scoped lang="scss">
@use 'vuetify/lib/styles/settings/colors' as *;
@use 'vuetify/lib/components/VGrid/mixins' as *;
@use '/resources/css/variables' as v;

.v-toolbar {
    transition: background-color 0.3s ease;
    &.inverted {
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }
}
.equal-spacer {
    width: 48px;
    height: 48px;
}
.app-bar-container {
    max-width: 1280px;
    display: flex;
    flex-grow: 1;
    height: 100%;
    align-items: center;

    &.top-bar {
        .tb-item {
            display: flex;
            gap: 0.8rem;
            align-items: center;
            font-size: 0.975rem;
        }
    }

    .app-bar-menu-items {
        display: flex;
        transition: color 0.3s ease;
        cursor: pointer;
        align-items: center;

        .app-bar-menu-item {
            position: relative;
            padding: 12px 18px;

            &:hover {
                color: rgb(var(--v-theme-cis-accent-color)) !important;
            }
            &.active {
                color: rgb(var(--v-theme-cis-accent-color)) !important;
                &::after {
                    position: absolute;
                    content: '.';
                    bottom: -8px;
                    left: 0;
                    font-size: 2rem;
                    width: 100%;
                    display: flex;
                    justify-content: center;
                    color: rgb(var(--v-theme-cis-accent-color)) !important;
                }
            }
        }
    }
}

.inverted {
    .app-bar-menu-items {
        color: white;
        .app-bar-menu-item {
            &:hover,
            &.active {
                opacity: 70%;
                color: white !important;
                &::after {
                    color: white !important;
                }
            }
        }
    }
}

.shrink {
    :deep(.v-toolbar__content) {
        height: 0 !important;
        opacity: 0;

        .v-toolbar-title {
            padding-bottom: 0 !important;
        }
    }
}

.v-app-bar {
    :deep(.v-toolbar__content) {
        @include make-container-max-widths;

        & {
            max-width: v.$container-max-width !important;
            margin-inline: auto;
            transition-property:
                height, width, transform, left, right, top, bottom, box-shadow, opacity;
        }
    }

    &.green-bar {
        :deep(.v-toolbar__content) {
            background-color: rgb(var(--v-theme-cis-accent-color))!important;
            max-width: 100%!important;
            .app-bar-container {
                margin-inline: auto;
            }
        }
    }

    :deep(.v-toolbar-title) {
        height: 100%;
    }

    :deep(.v-toolbar__extension) {
        display: flex;
        justify-content: center;
        border-top: 1px solid rgba(0, 0, 0, 0.05);
    }
}

:deep(.v-list-item-title) {
    .warning & {
        color: rgb(var(--v-theme-cis-accent-color)) !important;
    }
}

:deep(a:hover, a:focus, a:active) {
    .v-list-item-title {
        color: rgb(var(--v-theme-cis-accent-color));
    }
}
</style>
